import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { inject, NgZone, PLATFORM_ID, Service } from '@angular/core';

/**
 * PlatformService — the single SSR-safety gate for the entire /v2 system.
 *
 * Every browser-only API (localStorage, window, requestAnimationFrame, …)
 * is reached THROUGH this service so components/services never touch
 * `window`/`document` directly. Fully isolated from v1.
 */
@Service()
export class PlatformService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  readonly document = inject(DOCUMENT);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isServer = isPlatformServer(this.platformId);

  /** The real `window`, or `null` on the server. */
  get window(): (Window & typeof globalThis) | null {
    return this.isBrowser ? (this.document.defaultView as Window & typeof globalThis) : null;
  }

  /** rAF that degrades to a microtask on the server (keeps callers synchronous-safe). */
  raf(cb: () => void): void {
    const w = this.window;
    if (w?.requestAnimationFrame) w.requestAnimationFrame(() => cb());
    else queueMicrotask(cb);
  }

  /**
   * THE repeating-timer primitive for the whole app — use this, never a bare
   * `setInterval`.
   *
   * zone.js books a periodic timer as a macrotask that stays pending until it is
   * cleared, so a single live `setInterval` anywhere in the tree means
   * `ApplicationRef.isStable()` NEVER emits `true`. Hydration waits on exactly
   * that signal and gives up after 10s with NG0506 — which is why a one-second
   * countdown on the landing page silently breaks hydration for the whole page.
   *
   * Arming the timer OUTSIDE the Angular zone keeps it off that ledger, and
   * `zone.run` around each tick puts the callback back inside, so change
   * detection, HTTP calls and signal writes behave exactly as they did with a
   * plain `setInterval`. Only stability bookkeeping changes.
   *
   * Returns the timer id, or `0` on the server (where no timer is armed) — pass
   * it to {@link clearInterval}, which tolerates `0`/`null`.
   */
  interval(callback: () => void, ms: number): number {
    const w = this.window;
    if (!w) return 0;
    return this.zone.runOutsideAngular(() => w.setInterval(() => this.zone.run(callback), ms));
  }

  /** Cancel a timer from {@link interval}. Safe with `0`, `null` or `undefined`. */
  clearInterval(id: number | null | undefined): void {
    if (id) this.window?.clearInterval(id);
  }

  /** matchMedia guard — returns `false` on the server. */
  matches(query: string): boolean {
    return !!this.window?.matchMedia?.(query)?.matches;
  }

  prefersReducedMotion(): boolean {
    return this.matches('(prefers-reduced-motion: reduce)');
  }

  prefersDark(): boolean {
    return this.matches('(prefers-color-scheme: dark)');
  }

  /**
   * Force a browser download of an in-memory {@link Blob} under a chosen
   * filename (e.g. a server-rendered PDF fetched with the bearer). Uses a
   * programmatic `<a download>` click — reliable even after an `await`, unlike
   * `window.open`, which browsers popup-block once the user-gesture context is
   * lost. The object URL is revoked on the next tick (after the download has
   * grabbed it) so nothing leaks. SSR-safe (no-op on the server); returns
   * `false` when unavailable or the blob is empty. The `filename` may be
   * Arabic or English — it is written verbatim to the `download` attribute.
   */
  downloadBlob(blob: Blob | null | undefined, filename: string): boolean {
    const w = this.window;
    if (!this.isBrowser || !w?.URL?.createObjectURL || !blob || !blob.size) return false;
    const url = w.URL.createObjectURL(blob);
    try {
      this.clickAnchor({ href: url, download: filename || 'download' });
    } finally {
      // Revoke on the next tick so the click has grabbed the blob first.
      w.setTimeout(() => w.URL.revokeObjectURL(url), 0);
    }
    return true;
  }

  /**
   * Open an already-navigable URL (a direct server link, not an authenticated
   * byte-stream) in a new tab via a programmatic anchor click — steadier than
   * `window.open` after an `await`, and lets us hint a download `filename` for
   * same-origin links. SSR-safe (no-op on the server); returns `false` off-browser.
   */
  openUrl(url: string, opts: { download?: string } = {}): boolean {
    if (!this.isBrowser || !url) return false;
    this.clickAnchor({ href: url, target: '_blank', rel: 'noopener noreferrer', download: opts.download });
    return true;
  }

  /**
   * Redirect the CURRENT tab to an external URL — the same-tab counterpart to
   * {@link openUrl}. This is THE single helper for handing off to a hosted
   * online-payment gateway (Tap): the checkout page must OWN the tab so that,
   * once the charge settles, the gateway's redirect back to `/payments/status`
   * lands in this same tab and the return page resumes the flow — no orphaned
   * tab, no pop-up blocker. Navigation goes through `location.assign` (keeps the
   * pre-payment page in history; a full external navigation, so `window.opener`
   * is never exposed — no `noopener` needed). SSR-safe (no-op on the server);
   * returns `false` off-browser or for an empty URL. Call inside a user gesture.
   */
  redirectExternal(url: string | null | undefined): boolean {
    const w = this.window;
    if (!this.isBrowser || !w || !url) return false;
    w.location.assign(url);
    return true;
  }

  /** Build a detached anchor, click it, and remove it (browser-only; caller guards). */
  private clickAnchor(attrs: { href: string; download?: string; target?: string; rel?: string }): void {
    const doc = this.document;
    const a = doc.createElement('a');
    a.href = attrs.href;
    if (attrs.download != null) a.download = attrs.download;
    if (attrs.target) a.target = attrs.target;
    a.rel = attrs.rel ?? 'noopener';
    a.style.display = 'none';
    doc.body.appendChild(a);
    a.click();
    a.remove();
  }
}
